import React from 'react';
import { Page, Text, View, Document } from '@react-pdf/renderer';
import { IEmergencyTreatmentReport, IEmergencyTreatmentsModel } from '../../../models/IEmergencyTreatment';
import { styles } from "./EmergencyTreatmentPDFStyle";
import { EmergencyTreatmentPDFPatientAndAccountableCareProvider } from "./EmergencyTreatmentPDFPatientAndAccountableCareProvider";
import { EmergencyTreatmentPDFTreatingCareProvider } from "./EmergencyTreatmentPDFTreatingCareProvider";
import { EmergencyTreatmentPDFApplicationInformation } from "./EmergencyTreatmentPDFApplicationInformation";
import { EmergencyTreatmentPDFTreatmentsTable } from "./EmergencyTreatmentPDFTreatmentsTable";
import { IAddress } from '../../../models/IAddress';

interface IEmergencyTreatmentPDF {
    emergencyTreatmentReport: IEmergencyTreatmentReport;
    patientSocialSecurityNumber: string;
    patientName: string;
    reportedTreatments: IEmergencyTreatmentsModel | undefined;
}

export const EmergencyTreatmentPDF: React.FunctionComponent<IEmergencyTreatmentPDF> = ({ emergencyTreatmentReport, patientSocialSecurityNumber, patientName, reportedTreatments }) => {

    const renderInformationField = (label: string, value: string | undefined) => {
        return (
            <View style={[styles.smallText, styles.informationFieldWrapper]} >
                <View style={{ width: "40%" }}>
                    <Text style={styles.label}>{label}</Text>
                </View>
                <View style={{ width: "60%" }}>
                    <Text>{undefinedOrEmpty(value) ? "Saknas" : value}</Text>
                </View>
            </View>
        )
    }

    const formatAddress = (address: IAddress | undefined): string => {
        if (undefinedOrEmpty(address?.address) || undefinedOrEmpty(address?.zipCode) || undefinedOrEmpty(address?.city)) {
            return "Saknas";
        }
        return `${address?.address?.trim()}, ${address?.zipCode} ${address?.city}`;
    }

    const undefinedOrEmpty = (value: string | undefined): boolean => {
        return value === undefined || value === "" || value === null;
    }

    return (
        <Document>
            <Page size="A4">
                <View style={styles.pageWrapper}>
                    <Text style={styles.headerOne} >Akuträkning</Text>
                    <View style={styles.flexComponent}>
                        <EmergencyTreatmentPDFPatientAndAccountableCareProvider
                            accountableCareProviderAddress={emergencyTreatmentReport.accountableCareProvider.address}
                            accountableCareProviderName={emergencyTreatmentReport.accountableCareProvider.name}
                            formatAddress={formatAddress}
                            patientSocialSecurityNumber={patientSocialSecurityNumber}
                            patientName={patientName}
                            renderInformationField={renderInformationField}
                        />
                        <EmergencyTreatmentPDFTreatingCareProvider
                            dentistName={emergencyTreatmentReport.carePerformer?.carePerformerName ?? emergencyTreatmentReport.dentistName}
                            dentistPhoneNumber={""}
                            externalId={emergencyTreatmentReport.externalId}
                            formatAddress={formatAddress}
                            renderInformationField={renderInformationField}
                            treatingCareProviderAddress={emergencyTreatmentReport.treatingCareProvider.address}
                            treatingCareProviderName={emergencyTreatmentReport.treatingCareProvider.name}
                        />
                    </View>
                    <View style={styles.largeBox}>
                        <Text style={[styles.headerTwo, { marginBottom: "5px" }]}>Ansökningsinformation</Text>
                        <View style={styles.flexComponent}>
                            <EmergencyTreatmentPDFApplicationInformation
                                billable={emergencyTreatmentReport.billable}
                                dateSent={emergencyTreatmentReport.dateSent}
                                dateOfTreatment={emergencyTreatmentReport.dateOfTreatment}
                                extraInfo={emergencyTreatmentReport.extraInfo}
                                formatAddress={formatAddress}
                                renderInformationField={renderInformationField}
                                treatmentNumber={emergencyTreatmentReport.treatmentNumber}
                            />
                        </View>
                        <EmergencyTreatmentPDFTreatmentsTable
                            reportedTreatments={reportedTreatments}
                        />
                    </View>
                </View>
            </Page>
        </Document>
    )

}